import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { createPortal } from 'react-dom';
import { cn } from '../lib/utils';
import {
  resolveTooltipPlacement,
  type TooltipAlign,
  type TooltipPlacement,
  type TooltipSide,
} from './tooltipPositioning';

export interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
  /** Preferred side. The tooltip flips automatically when that side does not fit. */
  position?: TooltipSide;
  /** Cross-axis alignment relative to the trigger. */
  align?: TooltipAlign;
  delay?: number;
  className?: string;
  disabled?: boolean;
  sideOffset?: number;
  viewportPadding?: number;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  wrapperClassName?: string;
  panelClassName?: string;
  showArrow?: boolean;
}

interface TooltipBubbleProps {
  id: string;
  content: React.ReactNode;
  placement: TooltipPlacement | null;
  className?: string;
  panelClassName?: string;
  showArrow?: boolean;
}

const DEFAULT_PANEL_CLASSES = [
  'relative max-w-[min(20rem,calc(100vw-1rem))] whitespace-normal break-words',
  'rounded-lg border border-border-subtle bg-surface-lifted px-2.5 py-1.5',
  'text-xs font-medium leading-snug text-text-primary shadow-lg',
].join(' ');

const TooltipBubble = React.forwardRef<HTMLDivElement, TooltipBubbleProps>(function TooltipBubble(
  { id, content, placement, className, panelClassName, showArrow = true },
  ref,
) {
  const side = placement?.side ?? 'top';
  const motionOffset = side === 'top'
    ? { x: 0, y: 4 }
    : side === 'bottom'
      ? { x: 0, y: -4 }
      : side === 'left'
        ? { x: 4, y: 0 }
        : { x: -4, y: 0 };

  const arrowClasses = cn(
    'absolute -z-10 h-2 w-2 rotate-45 bg-surface-lifted',
    side === 'top' && '-bottom-1 border-b border-r border-border-subtle',
    side === 'bottom' && '-top-1 border-l border-t border-border-subtle',
    side === 'left' && '-right-1 border-r border-t border-border-subtle',
    side === 'right' && '-left-1 border-b border-l border-border-subtle',
  );

  const arrowStyle: React.CSSProperties | undefined = !placement
    ? undefined
    : side === 'top' || side === 'bottom'
      ? { left: placement.arrowX, marginLeft: -4 }
      : { top: placement.arrowY, marginTop: -4 };

  return (
    <motion.div
      ref={ref}
      role="tooltip"
      id={id}
      data-tooltip-side={side}
      initial={{ opacity: 0, ...motionOffset }}
      animate={{ opacity: placement ? 1 : 0, x: 0, y: 0 }}
      exit={{ opacity: 0, ...motionOffset }}
      transition={{ duration: 0.12 }}
      className={cn('pointer-events-none fixed z-[9999]', className)}
      style={{
        left: placement?.left ?? 0,
        top: placement?.top ?? 0,
        visibility: placement ? 'visible' : 'hidden',
      }}
    >
      <div className={panelClassName || DEFAULT_PANEL_CLASSES}>
        {content}
        {showArrow && <span aria-hidden="true" className={arrowClasses} style={arrowStyle} />}
      </div>
    </motion.div>
  );
});

const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  position = 'top',
  align = 'center',
  delay = 300,
  className,
  disabled = false,
  sideOffset = 8,
  viewportPadding = 8,
  open,
  onOpenChange,
  wrapperClassName,
  panelClassName,
  showArrow = true,
}) => {
  const isControlled = typeof open === 'boolean';
  const [internalOpen, setInternalOpen] = useState(false);
  const [placement, setPlacement] = useState<TooltipPlacement | null>(null);
  const isOpen = isControlled ? open : internalOpen;

  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<number | null>(null);
  const hoveredRef = useRef(false);
  const focusedRef = useRef(false);
  const id = useId();

  const clearTimer = useCallback(() => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const setOpen = useCallback((nextOpen: boolean) => {
    if (nextOpen && disabled) return;
    if (!isControlled) setInternalOpen(nextOpen);
    onOpenChange?.(nextOpen);
  }, [disabled, isControlled, onOpenChange]);

  const computePosition = useCallback(() => {
    const trigger = triggerRef.current;
    const bubble = tooltipRef.current;
    if (!trigger || !bubble) return;

    setPlacement(resolveTooltipPlacement({
      triggerRect: trigger.getBoundingClientRect(),
      tooltipSize: bubble.getBoundingClientRect(),
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      preferredSide: position,
      align,
      sideOffset,
      viewportPadding,
    }));
  }, [align, position, sideOffset, viewportPadding]);

  const show = useCallback(() => {
    if (disabled) return;
    clearTimer();
    timeoutRef.current = window.setTimeout(() => setOpen(true), delay);
  }, [clearTimer, delay, disabled, setOpen]);

  const hide = useCallback(() => {
    clearTimer();
    setOpen(false);
  }, [clearTimer, setOpen]);

  useEffect(() => clearTimer, [clearTimer]);

  useEffect(() => {
    if (!disabled) return;
    clearTimer();
    setOpen(false);
  }, [clearTimer, disabled, setOpen]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setPlacement(null);
      return;
    }

    const frame = window.requestAnimationFrame(computePosition);
    return () => window.cancelAnimationFrame(frame);
  }, [computePosition, content, isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const reposition = () => window.requestAnimationFrame(computePosition);
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition, { passive: true });

    const observer = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(reposition)
      : null;
    if (triggerRef.current) observer?.observe(triggerRef.current);
    if (tooltipRef.current) observer?.observe(tooltipRef.current);

    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
      observer?.disconnect();
    };
  }, [computePosition, isOpen]);

  if (content === null || content === undefined || content === '') {
    return <>{children}</>;
  }

  const trigger = (
    <span
      ref={triggerRef}
      className={cn('inline-block', wrapperClassName)}
      aria-describedby={isOpen ? id : undefined}
      onMouseEnter={() => {
        hoveredRef.current = true;
        show();
      }}
      onMouseLeave={() => {
        hoveredRef.current = false;
        if (!focusedRef.current) hide();
      }}
      onFocus={() => {
        focusedRef.current = true;
        show();
      }}
      onBlur={() => {
        focusedRef.current = false;
        if (!hoveredRef.current) hide();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') hide();
      }}
    >
      {children}
    </span>
  );

  const portal = typeof document !== 'undefined'
    ? createPortal(
      <AnimatePresence>
        {isOpen && (
          <TooltipBubble
            ref={tooltipRef}
            id={id}
            content={content}
            placement={placement}
            className={className}
            panelClassName={panelClassName}
            showArrow={showArrow}
          />
        )}
      </AnimatePresence>,
      document.body,
    )
    : null;

  return <>{trigger}{portal}</>;
};

interface NativeTooltipState {
  element: HTMLElement;
  content: string;
  preferredSide: TooltipSide;
}

const NATIVE_TOOLTIP_SELECTOR = '[title], [data-ax1-native-tooltip]';

function readNativeTooltip(target: EventTarget | null): NativeTooltipState | null {
  if (!(target instanceof Element)) return null;
  const element = target.closest<HTMLElement>(NATIVE_TOOLTIP_SELECTOR);
  if (!element || element.tagName === 'IFRAME' || element.dataset.tooltipNative === 'true') return null;

  const title = element.getAttribute('title')?.trim();
  const storedContent = element.dataset.ax1NativeTooltip?.trim();
  const content = title || storedContent;
  if (!content) return null;

  if (title) {
    element.dataset.ax1NativeTooltip = title;
    element.removeAttribute('title');
  }

  const side = element.dataset.tooltipSide;
  const preferredSide: TooltipSide = side === 'bottom' || side === 'left' || side === 'right'
    ? side
    : 'top';

  const interactiveSelector = 'button, a, input, select, textarea, [role="button"], [role="link"]';
  const hasVisibleName = Boolean(element.textContent?.trim());
  if (element.matches(interactiveSelector) && !element.getAttribute('aria-label') && !hasVisibleName) {
    element.setAttribute('aria-label', content);
    element.dataset.ax1TooltipGeneratedLabel = 'true';
  }

  return { element, content, preferredSide };
}

function restoreNativeTooltip(element: HTMLElement | null) {
  if (!element) return;
  const storedContent = element.dataset.ax1NativeTooltip;
  if (storedContent && !element.hasAttribute('title') && element.isConnected) {
    element.setAttribute('title', storedContent);
  }
  delete element.dataset.ax1NativeTooltip;
}

/**
 * Compatibility bridge for legacy native `title` attributes. It suppresses the
 * browser-default bubble on first interaction and renders the shared AX1
 * tooltip instead. Iframe titles are intentionally preserved as accessibility
 * names. New UI should use `<Tooltip>` directly.
 */
export function TooltipProvider({ children }: React.PropsWithChildren) {
  const [active, setActive] = useState<NativeTooltipState | null>(null);
  const [placement, setPlacement] = useState<TooltipPlacement | null>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const pendingRef = useRef<HTMLElement | null>(null);
  const timeoutRef = useRef<number | null>(null);
  const id = useId();

  const clearTimer = useCallback(() => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const cancelPending = useCallback(() => {
    const pendingElement = pendingRef.current;
    clearTimer();
    pendingRef.current = null;
    restoreNativeTooltip(pendingElement);
  }, [clearTimer]);

  const close = useCallback(() => {
    cancelPending();
    restoreNativeTooltip(active?.element ?? null);
    setActive(null);
    setPlacement(null);
  }, [active?.element, cancelPending]);

  const scheduleOpen = useCallback((next: NativeTooltipState, delayMs: number) => {
    cancelPending();
    if (active && active.element !== next.element) {
      restoreNativeTooltip(active.element);
      setActive(null);
      setPlacement(null);
    }
    pendingRef.current = next.element;
    timeoutRef.current = window.setTimeout(() => {
      if (!next.element.isConnected) return;
      setPlacement(null);
      setActive(next);
      pendingRef.current = null;
    }, delayMs);
  }, [active, cancelPending]);

  const computePosition = useCallback(() => {
    if (!active || !bubbleRef.current || !active.element.isConnected) return;
    setPlacement(resolveTooltipPlacement({
      triggerRect: active.element.getBoundingClientRect(),
      tooltipSize: bubbleRef.current.getBoundingClientRect(),
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      preferredSide: active.preferredSide,
      sideOffset: 8,
      viewportPadding: 8,
    }));
  }, [active]);

  useLayoutEffect(() => {
    if (!active) return;
    const frame = window.requestAnimationFrame(computePosition);
    return () => window.cancelAnimationFrame(frame);
  }, [active, computePosition]);

  useEffect(() => {
    const handlePointerOver = (event: PointerEvent) => {
      const next = readNativeTooltip(event.target);
      if (!next || next.element === active?.element || next.element === pendingRef.current) return;
      scheduleOpen(next, 300);
    };

    const handlePointerOut = (event: PointerEvent) => {
      const current = readNativeTooltip(event.target);
      if (!current) return;
      if (event.relatedTarget instanceof Node && current.element.contains(event.relatedTarget)) return;
      if (current.element === active?.element || current.element === pendingRef.current) close();
    };

    const handleFocusIn = (event: FocusEvent) => {
      const next = readNativeTooltip(event.target);
      if (next) scheduleOpen(next, 0);
    };

    const handleFocusOut = (event: FocusEvent) => {
      const current = readNativeTooltip(event.target);
      if (!current) return;
      if (event.relatedTarget instanceof Node && current.element.contains(event.relatedTarget)) return;
      if (current.element === active?.element || current.element === pendingRef.current) close();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };

    document.addEventListener('pointerover', handlePointerOver);
    document.addEventListener('pointerout', handlePointerOut);
    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('pointerover', handlePointerOver);
      document.removeEventListener('pointerout', handlePointerOut);
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [active?.element, close, scheduleOpen]);

  useEffect(() => {
    if (!active) return;
    const reposition = () => window.requestAnimationFrame(computePosition);
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition, { passive: true });

    const observer = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(reposition)
      : null;
    observer?.observe(active.element);
    if (bubbleRef.current) observer?.observe(bubbleRef.current);

    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
      observer?.disconnect();
    };
  }, [active, computePosition]);

  useEffect(() => () => {
    cancelPending();
    restoreNativeTooltip(active?.element ?? null);
  }, [active?.element, cancelPending]);

  const portal = typeof document !== 'undefined'
    ? createPortal(
      <AnimatePresence>
        {active && (
          <TooltipBubble
            ref={bubbleRef}
            id={id}
            content={active.content}
            placement={placement}
          />
        )}
      </AnimatePresence>,
      document.body,
    )
    : null;

  return <>{children}{portal}</>;
}

export default Tooltip;
